import type { CSSProperties, ReactElement } from "react";
import { color, radius, space, type RadiusKey } from "@tokens/index";

/**
 * Skeleton: the loading state in the shape of the content.
 *
 * `SkeletonBlock` is one flat `bg.surface3` block. The global stylesheet
 * drives its opacity pulse through `[data-skeleton]`, so the block carries
 * no animation of its own and goes static under `prefers-reduced-motion`.
 *
 * `SkeletonRows` is a labelled `role="status"` group of rows. `shape="list"`
 * renders a glyph slot and two text lines per row; `shape="card"` renders a
 * bordered card per row.
 */

export interface SkeletonBlockProps {
    /** Default `"100%"`. */
    width?: string | number;
    /** Default `12`. */
    height?: string | number;
    /** Radius key. Default `"sm"`. */
    radius?: RadiusKey;
    style?: CSSProperties;
}

export function SkeletonBlock({
    width = "100%",
    height = 12,
    radius: radiusKey = "sm",
    style,
}: SkeletonBlockProps): ReactElement {
    return (
        <span
            aria-hidden="true"
            data-skeleton=""
            style={{
                display: "block",
                width,
                height,
                borderRadius: radius[radiusKey],
                background: color.bg.surface3,
                flexShrink: 0,
                ...style,
            }}
        />
    );
}

export type SkeletonShape = "list" | "card";

export interface SkeletonRowsProps {
    /** Default `3`. */
    rows?: number;
    /** Accessible label, for example "Loading queues". */
    label: string;
    /** Default `"list"`. */
    shape?: SkeletonShape;
    /** Minimum height of one card in px. Applies to `shape="card"`. Default `56`. */
    rowHeight?: number;
}

const groupStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[2],
    width: "100%",
    minWidth: 0,
};

const listRowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    padding: `${space[2]} ${space[3]}`,
    minWidth: 0,
};

const linesStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[1],
    flex: 1,
    minWidth: 0,
};

function cardRowStyle(rowHeight: number): CSSProperties {
    return {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: space[2],
        minHeight: rowHeight,
        padding: `${space[3]} ${space[4]}`,
        borderRadius: radius.lg,
        border: `1px solid ${color.border.subtle}`,
        background: color.bg.surface,
        minWidth: 0,
    };
}

/** Title and meta widths vary per row so the group does not read as a grid. */
function lineWidths(index: number): { readonly title: string; readonly meta: string } {
    return {
        title: `${String(58 + ((index * 17) % 30))}%`,
        meta: `${String(30 + ((index * 23) % 40))}%`,
    };
}

export function SkeletonRows({
    rows = 3,
    label,
    shape = "list",
    rowHeight = 56,
}: SkeletonRowsProps): ReactElement {
    return (
        <div
            role="status"
            aria-live="polite"
            aria-label={label}
            aria-busy="true"
            data-skeleton-rows={shape}
            style={groupStyle}
        >
            <span data-visually-hidden="">{label}</span>
            {Array.from({ length: rows }, (_, index) => {
                const widths = lineWidths(index);
                if (shape === "card") {
                    return (
                        <div key={index} aria-hidden="true" style={cardRowStyle(rowHeight)}>
                            <SkeletonBlock width={widths.title} height={14} />
                            <SkeletonBlock width={widths.meta} height={12} />
                        </div>
                    );
                }
                return (
                    <div key={index} aria-hidden="true" style={listRowStyle}>
                        <SkeletonBlock width={16} height={16} radius="full" />
                        <span style={linesStyle}>
                            <SkeletonBlock width={widths.title} height={12} />
                            <SkeletonBlock width={widths.meta} height={9} />
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
